import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EngagementProviderWithoutConfig from '@atlassiansox/engagekit-ts/controllers/engagement-provider/index';

import { ProductIds } from '@atlassian/ipm-choreographer/constants';

import { getAnalyticsWebClient, getWorkspaceId } from '@confluence/analytics-web-client';
import { useAnalyticsEnvironment } from '@confluence/choreographer-services/entry-points/useAnalyticsEnvironment.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

export function EngagementProviderComponent(props: PropsWithChildren<{}>): React.JSX.Element {
	const { cloudId, locale, userId, activationId, environment } = useSessionData();

	const workspaceId = getWorkspaceId(cloudId, activationId);

	if (sessionStorage.getItem(keys.DISABLE_ENGAGEMENT_FOR_TESTS) === 'true') {
		return <>{props.children}</>;
	}

	const config = {
		cloudId,
		aaid: userId as string,
		locale,
		product: 'confluence',
		stargateUrl: '/gateway/api',
		workspaceId,
		isFedRAMPEnvironment: false,
	};

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const envType = useAnalyticsEnvironment(environment);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const coordinationOptions = useMemo(
		() => ({
			enableChoreographer: true,
			envType,
			additionalData: { sourceProductId: ProductIds.CONFLUENCE },
		}),
		[envType],
	);

	return (
		<EngagementProviderWithoutConfig
			{...props}
			// eslint-disable-next-line react/no-children-prop
			children={<>{props.children}</>}
			config={config}
			analyticsClientInstance={getAnalyticsWebClient()}
			coordinationOptions={coordinationOptions}
		/>
	);
}
export declare namespace EngagementProviderComponent {
	export let propTypes: {
		children: PropTypes.Requireable<PropTypes.ReactElementLike>;
		locale: PropTypes.Requireable<string>;
	};
}

EngagementProviderComponent.propTypes = {
	children: PropTypes.element,
	locale: PropTypes.string,
};
